import { create } from 'zustand';

import { Auth as FirebaseAuth, onAuthStateChanged } from 'firebase/auth';

import { UserDatabase } from '~/types/user';
import { getAdventurerURL } from '~/config/endpoints.config';

import { AuthStatus } from './interface/authStatus.interface';
import { AuthState } from './interface/authState.interface';

import { minifyAuth } from './utils/minifyAuth.utils';
import { INITIAL_STATE } from './utils/initialize.utils';

import { loginWithPassword, loginAsGuest, loginWithGoogle, handleForgotPassword } from './functions/login';
import { signup } from './functions/signup';
import { logout } from './functions/logout';
import { resetPassword, updatePassword } from './functions/updateEmailPassword';
import { deleteAccount } from './functions/deleteAccount';
import { reauthenticateWithGoogle, reauthenticateWithPassword } from './functions/reauthenticate';
import { requestPasswordReset } from './functions/requestPasswordReset';
import { AuthError } from './interface';

/**
 * The store that keep informations about the authentification
 */
export const useAuth = create<AuthState>((set: any, get: any) => ({
    ...INITIAL_STATE,

    /**
     * Send a reset link to the given email.
     */
    sendResetLink: async (_firebaseAuth: FirebaseAuth, email: string) => {
        try {
            await requestPasswordReset(email);
            return {
                type: 'success',
                message: 'A reset email has been sent to you.',
            };
        } catch (error) {
            return {
                type: 'error',
                message: 'Please provide your email to reset your password.',
            };
        }
    },

    /**
     * This function attempts to register an user and
     * modifies the authentification state accordingly.
     */
    signup: async (email: string, password: string, confirmPassword?: string) =>
        signup(set, get, email, password, confirmPassword),

    /**
     * This function attempts to authenticate an user and
     * modifies the authentification state accordingly.
     */
    login: async (email: string, password: string) => loginWithPassword(set, get, email, password),

    /**
     * This function attempts to authenticate an user and
     * modifies the authentification state accordingly.
     */
    loginWithPassword: async (email: string, password: string) => loginWithPassword(set, get, email, password),

    /**
     * This function attempts to authenticate a user with google and
     * modifies the authentification state accordingly.
     */
    loginWithGoogle: async () => loginWithGoogle(set, get),

    /**
     * This function attempts to authenticate a user as guest and
     * modifies the authentification state accordingly.
     */
    loginAsGuest: () => loginAsGuest(set, get),

    /**
     * This function attempts to authenticate a user as guest and
     * modifies the authentification state accordingly.
     */
    loginWithoutAccount: () => {
        set((state: AuthState) => ({
            ...INITIAL_STATE,
            firebaseAuth: state.firebaseAuth,
            initialLoading: false,
            loading: false,
            auth: {
                isLogin: false,
            },
        }));
    },

    /**
     * This function modifies the auth status after that it suceeds.
     */
    loginSuccess: (auth: any) =>
        set((state: AuthState) => ({
            ...INITIAL_STATE,
            initialLoading: false,
            firebaseAuth: state.firebaseAuth,
            auth,
            loading: false,
        })),

    /**
     * This function modifies the auth status after that it fails.
     */
    loginFailure: (errorCode: AuthError) =>
        set((state: AuthState) => ({
            ...state,
            email: '',
            password: '',
            loading: false,
            errorCode,
            passwordErrorCode: undefined,
            emailErrorCode: undefined,
        })),

    /**
     * This function prints an error message relatives to an email error.
     */
    loginEmailFailure: (errorCode: AuthError) =>
        set((state: AuthState) => ({
            ...state,
            password: '',
            loading: false,
            errorCode,
            passwordErrorCode: undefined,
            emailErrorCode: errorCode,
        })),

    /**
     * This function prints an error message relatives to a password error.
     */
    loginPasswordFailure: (errorCode: AuthError) =>
        set((state: AuthState) => ({
            ...state,
            password: '',
            loading: false,
            errorCode,
            passwordErrorCode: errorCode,
            emailErrorCode: undefined,
        })),

    /**
     * Sign Out the current user.
     */
    logout: () => logout(set, get),

    /**
     * Sign Out the current user.
     */
    deleteAccount: () => deleteAccount(set, get),

    /**
     * Update the password of the current user.
     */
    updatePassword: async (password: string) => updatePassword(set, get, password),

    /**
     * Reset the password.
     */
    onResetPassword: (password: string, actionCode: string, continueUrl: string, lang: string) => {
        resetPassword(set, get, password, actionCode, continueUrl, lang);
    },

    /**
     * If the user click on the forgot password button
     */
    onForgotPassword: () => handleForgotPassword(set, get),

    /**
     * Change status.
     */
    changeStatus: (status: AuthStatus) => set((state: AuthState) => ({ ...state, status })),

    /**
     * Change email.
     */
    changeEmail: (email: string) =>
        set((state: AuthState) => ({
            ...state,
            email,
            emailErrorCode: undefined,
        })),

    /**
     * Change password.
     */
    changePassword: (password: string) =>
        set((state: AuthState) => ({
            ...state,
            password,
            passwordErrorCode: undefined,
        })),

    /**
     * Reset auth status.
     */
    resetFields: () =>
        set((state: AuthState) => ({
            ...INITIAL_STATE,
            firebaseAuth: state.firebaseAuth,
            auth: state.auth,
            initialLoading: false,
        })),

    /**
     * This function modifies the auth status after that it suceeds.
     */
    resetError: () =>
        set((state: AuthState) => ({
            ...state,
            errorCode: undefined,
            emailErrorCode: undefined,
            passwordErrorCode: undefined,
        })),

    /**
     * Reauthenticate the current user with password.
     */
    reauthenticateWithPassword: async (email: string, password: string) =>
        reauthenticateWithPassword(set, get, email, password),

    /**
     * Reauthenticate the current user with google provider.
     */
    reauthenticateWithGoogle: async () => reauthenticateWithGoogle(set, get),

    /**
     * Listen for authentification state changes.
     */
    onAuthStateChanged: (firebaseAuth: FirebaseAuth) => {
        set({
            ...INITIAL_STATE,
            firebaseAuth,
            initialLoading: true,
            loading: true,
        });
        onAuthStateChanged(firebaseAuth, (auth) => {
            if (auth) {
                // Profil Firestore garanti pour toute session (connexion Google,
                // email/mot de passe, rechargement de page, lien direct). Les pages
                // signin/signup écrivent `users/{uid}` dans un `.then` APRÈS ce
                // basculement d'état ; si cette écriture n'aboutit pas, le compte
                // Auth existe sans profil et l'app reste sur « Chargement des
                // données utilisateur » à vie (gotcha #170). `ensureUser` est
                // idempotent (transaction : rien à faire si le profil existe) et
                // non bloquant : le snapshot de `useSyncUser` prend le relais.
                if (!auth.isAnonymous) {
                    const email = auth.email || '';
                    UserDatabase.ensureUser(auth.uid, email, getAdventurerURL(email))
                        .then((created) => {
                            if (created) console.warn(`[auth] users/${auth.uid} manquant → recréé`);
                        })
                        .catch((error) => console.error('[auth] ensureUser failed', error));
                }
                set({
                    ...INITIAL_STATE,
                    firebaseAuth,
                    initialLoading: false,
                    loading: false,
                    auth: minifyAuth(auth),
                });
            } else {
                set((state: AuthState) => ({
                    ...INITIAL_STATE,
                    firebaseAuth: state.firebaseAuth,
                    initialLoading: false,
                    loading: false,
                }));
            }
        });
    },
}));
